/* eslint-disable max-len */
import React from 'react';
import _, { uniqBy, truncate } from 'lodash';
import useSearchParams from '~/commons/hooks/useSearchParams';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-regular-svg-icons/faFileAlt';
import { faEyeSlash } from '@fortawesome/free-regular-svg-icons/faEyeSlash';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons/faTimesCircle';
import { env } from '~/commons/config';
import ListResource from './ListResource.jsx';
import ListCtrlActivates from './ListCtrlActivates.jsx';

const api = '/api/core/stocks';

const ListStocks = ({ design, segment, showrooms }) => {
  const [searchParams, setSearchParams] = useSearchParams(null);

  const filter = design ? { design: design._id } : {};

  const showrms = uniqBy(showrooms, 'orgId');

  if (searchParams.get('status')) {
    filter.status = searchParams.get('status');
  }
  if (searchParams.get('showroom')) {
    filter.orgId = searchParams.get('showroom');
  }

  return (
    <ListResource
      {...{
        searchable: [
          { value: 'All', label: 'All' },
          {
            value: 'photos',
            label: 'Picture',
            options: [
              { value: true, label: 'With Picture' },
              { value: false, label: 'Without Picture' },
            ],
          },
          {
            value: 'erpSync.success',
            label: 'Success Sync',
            options: [
              { value: 1, label: 'True' },
              { value: 0, label: 'False' },
            ],
          },
          { value: 'size', label: 'Size' },
          {
            value: 'arStatus',
            label: 'AR',
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Active but manual suppress' },
            ],
          },
        ],
        api,
        filter,
        sort: { itemCode: 1 },
        actions: {
          create: !design,
          fileExport: true,
          fileImport: design ? false : { url: '/api/core/stocks/batch/import' },
        },
        extraActions: [
          {
            type: 'import',
            api: {
              url: '/api/core/stocks/uploadArAssets',
            },
            fileOpts: {
              accept: '.zip',
            },
            children: 'Upload AR Assets',
            onSuccess: ({ data: { logs } }) => ({
              showIcon: false,
              children: (
                <div>{_.map(_.castArray(logs), (r, i) => (
                  <div key={i}>{JSON.stringify(r)}</div>
                ))}</div>
              ),
            }),
          },
        ],
        segment,
        cols: [
          {
            key: 'photos',
            getter: (v) => (v?.[0] ? (
                <img
                  src={v[0]}
                  alt={'s'}
                  style={{
                    maxWidth: '120px',
                    maxHeight: '120px',
                  }}
                />
            ) : (
              ''
            )),
          },
          {
            key: 'arActive',
            children: 'AR',
            sortable: false,
            getter(active, v) {
              if (v.arInherit && v.designVariantCode) {
                const variantIdx = v.designVariantCode ? _.findIndex(v?.design?.variation, (x) => x.itemCode === v.designVariantCode) : -1;
                const arImg = variantIdx === -1 || !v?.arInherit ? v?.arImg : v?.design?.variation?.[variantIdx]?.arImg;
                const arModel = variantIdx === -1 || !v?.arInherit ? v?.arModel : v?.design?.variation?.[variantIdx]?.arModel;
                const arActive = variantIdx === -1 || !v?.arInherit ? v?.arActive : v?.design?.variation?.[variantIdx]?.arActive;
                if (_.isEmpty(arImg) || _.isEmpty(arModel)) return <FA icon={faTimesCircle} />;
                if (!arActive) return <FA icon={faEyeSlash} />;
                return <FA icon={faCheckCircle} />;
              }
              if (_.isEmpty(v.arImg) || _.isEmpty(v.arModel)) return <FA icon={faTimesCircle} />;
              if (!active) return <FA icon={faEyeSlash} />;
              return <FA icon={faCheckCircle} />;
            },
          },
          {
            key: 'itemCode',
            sortable: true,
            children: 'Item Code',
          },
          {
            key: 'serialNo',
            sortable: true,
            children: 'Serial Number',
          },
          {
            key: 'status',
            sortable: true,
          },
          {
            key: 'design.coll.name',
            sortable: true,
            children: 'Collection',
          },
          {
            key: 'design.design',
            sortable: true,
            children: 'Design',
          },
          { key: 'size', sortable: true },
          {
            key: 'prices.currency',
            sortable: true,
            children: 'Currency',
          },
          {
            key: 'prices.retailPrice',
            sortable: true,
            children: 'Price',
            getter: (v) => (Number(v) || 0).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          },
          { key: 'location', sortable: true },
          { key: 'remarks', sortable: true, getter: (v) => truncate(v, { length: 120 }) },
          {
            key: 'erpSync.success',
            sortable: true,
            children: 'Success',
            getter: (v) => (typeof v === 'string' ? v : <FA icon={v ? faCheckCircle : faTimesCircle} />),
          },
        ],
        ctrls: [
          <select
            name="status"
            className="custom-select"
            key={0}
            value={(searchParams.get('status') ? searchParams.get('status') : '')}
            onChange={(e) => {
              setSearchParams({ status: e.target.value });
            }}>
            <option value="">All</option>
            {(env?.status?.stocks).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>,
          <select
            name="showroom"
            className="custom-select"
            key={1}
            value={(searchParams.get('showroom') ? searchParams.get('showroom') : '')}
            onChange={(e) => {
              setSearchParams({ showroom: e.target.value });
            }}>
            <option value="">All</option>
            {showrms.map((a) => (
              <option key={a._id} value={a.orgId}>
                {`${a.orgId} - ${a.countryCode}`}
              </option>
            ))}
          </select>,
        ],
      }}>
      {({ selected, refresh }) => !design && (
        <>
          <ListCtrlActivates api={api} selected={selected} onSuccess={refresh} />
          <a className="btn btn-link" href="/api/core/stocks/batch/import-example">
            <FA icon={faFileAlt} fixedWidth />
            &nbsp; Import Template
          </a>
        </>
      )}
    </ListResource>
  );
};

export default ListStocks;
